import type { TransactionCompletedResponse } from "@/types/api";

/** Parse image entity id from a completed transaction payload. */
function parseCompletedImageId(data: Record<string, unknown> | TransactionCompletedResponse): string | null {
  const d = data as Record<string, unknown>;
  const txId = (data as TransactionCompletedResponse).id;
  if (typeof txId === "string") {
    return txId;
  }

  if (typeof d["image_id"] === "string" && d["image_id"].length > 0) {
    return d["image_id"];
  }

  return null;
}

const coloredConsole = (text: string, color: string, isGroup?: boolean) => {
  if (isGroup) {
    // eslint-disable-next-line no-console
    console.group(`%c${text}`, `color:${color}`);
  } else {
    console.debug(`%c${text}`, `color:${color}`);
  }
};

const formatDurationSince = (editedTimestampSeconds: number) => {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const diffSeconds = Math.max(0, nowSeconds - editedTimestampSeconds);

  const minutes = Math.floor(diffSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days}d`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months}mo`;
  }

  const years = Math.floor(months / 12);
  return `${years}y`;
};

const SHORT_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const formatDate = (editedTimestampSeconds: number) => {
  const date = new Date(editedTimestampSeconds * 1000);
  const dd = date.getUTCDate();
  const mmm = SHORT_MONTH_NAMES[date.getUTCMonth()];
  const yyyy = date.getUTCFullYear();
  return `${dd} ${mmm} ${yyyy}`;
};

const splitFilenameForDisplay = (filename: string): { readonly base: string; readonly ext: string } => {
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex <= 0 || lastDotIndex >= filename.length - 1) {
    return { base: filename, ext: "" };
  }

  const parts = filename.split(".");
  if (parts.length >= 3) {
    const last = parts.at(-1) ?? "";
    const secondLast = parts.at(-2) ?? "";

    // Heuristic for compound extensions like ".tar.gz".
    if (last.length <= 3 && secondLast.length <= 3) {
      return {
        base: parts.slice(0, -2).join("."),
        ext: `.${parts.slice(-2).join(".")}`
      };
    }
  }

  return {
    base: filename.slice(0, lastDotIndex),
    ext: filename.slice(lastDotIndex)
  };
};

const getFileTypeFromFilename = (filename: string): string => {
  const ext = splitFilenameForDisplay(filename).ext.toLowerCase();

  switch (ext) {
    case ".pdf":
      return "application/pdf";
    case ".txt":
      return "text/plain";
    case ".doc":
      return "application/msword";
    case ".docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    default:
      console.warn(`Unknown file type: ${ext}`);
      return "text/plain";
  }
};

/** Encode <...> as {...} so the MDX parser doesn't treat angle brackets as tags. */
const encodeTags = (markdown: string): string => markdown.replaceAll(/<([^>]+)>/g, (_, content: string) => `{${content}}`);

/** Restore {...} to <...> for stored value. */
const decodeTags = (markdown: string): string => markdown.replaceAll(/\{([^}]+)\}/g, (_, content: string) => `<${content}>`);

/** `m:ss` for media controls (non-negative seconds; invalid → `0:00`). */
const formatMediaTimeSeconds = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }

  const s = Math.floor(seconds % 60);
  const m = Math.floor(seconds / 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/**
 * Presigned URLs often have no extension; Video.js needs a `type` hint.
 * Prefer an explicit MIME from the caller when the URL has no extension.
 */
const inferVideoMimeTypeFromUrl = (url: string): string => {
  let pathname = "";
  try {
    pathname = new URL(url).pathname.toLowerCase();
  } catch {
    const q = url.indexOf("?");
    pathname = (q === -1 ? url : url.slice(0, q)).toLowerCase();
  }

  if (pathname.endsWith(".m3u8")) {
    return "application/x-mpegURL";
  }

  if (pathname.endsWith(".mpd")) {
    return "application/dash+xml";
  }

  if (pathname.endsWith(".webm")) {
    return "video/webm";
  }

  if (pathname.endsWith(".ogg") || pathname.endsWith(".ogv")) {
    return "video/ogg";
  }

  if (pathname.endsWith(".mov") || pathname.endsWith(".qt")) {
    return "video/quicktime";
  }

  if (pathname.endsWith(".avi")) {
    return "video/x-msvideo";
  }

  if (pathname.endsWith(".mp4") || pathname.endsWith(".m4v")) {
    return "video/mp4";
  }

  return "video/mp4";
};

const fileExtensionFromContentType = (contentType: string): string => {
  const normalized = contentType.toLowerCase();
  if (normalized.includes("video/mp4")) {
    return "mp4";
  }

  if (normalized.includes("video/webm")) {
    return "webm";
  }

  if (normalized.includes("image/png")) {
    return "png";
  }

  if (normalized.includes("image/webp")) {
    return "webp";
  }

  if (normalized.includes("image/gif")) {
    return "gif";
  }

  if (normalized.includes("image/jpeg")) {
    return "jpg";
  }

  return "";
};

const fileExtensionFromUrlOrContentType = (url: string, contentType: string): string => {
  const fromContentType = fileExtensionFromContentType(contentType);
  if (fromContentType) {
    return fromContentType;
  }

  try {
    const { pathname } = new URL(url);
    const ext = splitFilenameForDisplay(pathname).ext.replace(/^\./, "");
    return ext;
  } catch {
    return "";
  }
};

const safeDownloadFilename = (name: string, extension: string): string => {
  const base =
    name
      .trim()
      .replaceAll(/[^\w.-]+/g, "-")
      .replaceAll(/^-+|-+$/g, "") || "asset";
  return extension && !base.toLowerCase().endsWith(`.${extension.toLowerCase()}`) ? `${base}.${extension}` : base;
};

const INVALID_CAPTURE_FILENAME_CHARS = /[<>:"/\\|?*]+/g;

/**
 * Upload filename for a captured video frame: `{video-stem}-frame-{iso-timestamp}.png`.
 * Stem from the last URL path segment (no extension), else `videoId`, else `video`.
 */
const captureFrameFilename = (videoUrl: string, videoId: string | null | undefined): string => {
  function sanitize(raw: string, maxLen: number): string {
    return raw.trim().replace(INVALID_CAPTURE_FILENAME_CHARS, "_").replace(/^\.+/, "").slice(0, maxLen);
  }

  let stem = "";

  const applyPathname = (pathname: string) => {
    const parts = pathname.split("/");
    let last = "";
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const seg = parts[i] ?? "";
      if (seg.length > 0) {
        last = seg;
        break;
      }
    }

    const noQuery = last.split("?")[0] ?? "";
    const dot = noQuery.lastIndexOf(".");
    const base = dot > 0 ? noQuery.slice(0, dot) : noQuery;
    const s = sanitize(base, 80);
    if (s.length > 0) {
      stem = s;
    }
  };

  if (videoUrl.length > 0) {
    try {
      applyPathname(new URL(videoUrl).pathname);
    } catch {
      const q = videoUrl.indexOf("?");
      const pathPart = q === -1 ? videoUrl : videoUrl.slice(0, q);
      applyPathname(pathPart.includes("/") ? pathPart : `/${pathPart}`);
    }
  }

  if (stem.length === 0 && typeof videoId === "string" && videoId.trim().length > 0) {
    stem = sanitize(videoId.trim(), 80);
  }

  if (stem.length === 0) {
    stem = "video";
  }

  const ts = new Date().toISOString().replaceAll(":", "-").replaceAll(".", "-");
  return `${stem}-frame-${ts}.png`;
};

const formatPlaybackRateLabel = (rate: number): string => `${rate}×`;

/** "Jun 5, 2:14 PM" — no year, for compact "last generated on ..." captions. Null on missing/invalid input. */
const formatShortDateTime = (iso: string | null | undefined): string | null => {
  if (!iso) {
    return null;
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

/**
 * Strip react-dropzone `noClick` / `noDrag` when only the rest should be spread onto a wrapper.
 */
const omitNoClickNoDrag = <T extends { noClick?: unknown; noDrag?: unknown }>(rootProps: T): Omit<T, "noClick" | "noDrag"> => {
  const { noClick: _noClick, noDrag: _noDrag, ...rest } = rootProps;
  return rest;
};

/** Number → `px` string; strings pass through (for CSS `width` / `maxWidth` in sx). */
const toCssWidth = (value: string | number): string | number => (typeof value === "number" ? `${value}px` : value);

const prependUniqueImageId = (imageIds: readonly string[] | undefined, imageId: string): string[] => [
  imageId,
  ...(imageIds ?? []).filter((id) => id !== imageId)
];

const prependImageIdIfMissing = (imageIds: readonly string[] | undefined, imageId: string): string[] => {
  const current = imageIds ?? [];
  return current.includes(imageId) ? [...current] : [imageId, ...current];
};

const appendUniqueImageId = (imageIds: readonly string[] | undefined, imageId: string): string[] => [
  ...(imageIds ?? []).filter((id) => id !== imageId),
  imageId
];

/** Ordered ids first, each once; remainder of `existing` unchanged order (storyboard `images[]`, etc.). */
const moveImageIdsToFront = (existing: readonly string[] | undefined, orderedFront: readonly string[]): string[] => {
  const front: string[] = [];
  const seen = new Set<string>();
  for (const id of orderedFront) {
    if (typeof id !== "string" || id === "" || seen.has(id)) {
      continue;
    }

    seen.add(id);
    front.push(id);
  }

  const exclude = new Set(front);
  const tail = (existing ?? []).filter((id) => !exclude.has(id));
  return [...front, ...tail];
};

const dataUrlToImageFile = async (dataUrl: string, filename: string): Promise<File> => {
  const blob = await fetch(dataUrl).then((r) => r.blob());
  return new File([blob], filename, { type: blob.type || "image/png" });
};

/** First candidate that is defined and non-empty, else first defined (possibly empty). */
const coalesceNonEmptyStringArrays = (...candidates: Array<readonly string[] | undefined>): readonly string[] => {
  for (const a of candidates) {
    if (a !== undefined && a.length > 0) {
      return a;
    }
  }

  for (const a of candidates) {
    if (a !== undefined) {
      return a;
    }
  }

  return [];
};

export {
  appendUniqueImageId,
  coalesceNonEmptyStringArrays,
  captureFrameFilename,
  coloredConsole,
  dataUrlToImageFile,
  decodeTags,
  encodeTags,
  fileExtensionFromContentType,
  fileExtensionFromUrlOrContentType,
  formatDate,
  formatDurationSince,
  formatMediaTimeSeconds,
  formatShortDateTime,
  formatPlaybackRateLabel,
  getFileTypeFromFilename,
  inferVideoMimeTypeFromUrl,
  moveImageIdsToFront,
  omitNoClickNoDrag,
  parseCompletedImageId,
  prependImageIdIfMissing,
  prependUniqueImageId,
  safeDownloadFilename,
  splitFilenameForDisplay,
  toCssWidth
};
